import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Chip,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  Link,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  alpha,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Tune as AdjustIcon,
  Edit as EditIcon,
  Add as AddIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  Refresh as RecalcIcon,
  KeyboardArrowDown as ChevronDownIcon,
  InfoOutlined as HelpIcon,
  ContentCopy as CopyIcon,
  QrCode2 as QrIcon,
  OpenInNew as OpenIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';
import QRCode from 'qrcode';
import { useNavigate, useParams } from 'react-router-dom';
import { useVenue } from '../../../../context/VenueContext';
import OpeningStockDialog from '../../../../components/venue/OpeningStockDialog';
import WineCatalogLinkDialog from '../../../../components/venue/WineCatalogLinkDialog';
import WineLinkStatusChip from '../../../../components/venue/WineLinkStatusChip';
import ItemStatusChip from '../../../../components/venue/ItemStatusChip';
import useRetireUndo from '../../../../components/venue/useRetireUndo';
import inventoryItemService, {
  extractInventoryError, extractInventoryErrorDetail,
} from '../../../../services/inventoryItemService';
import ItemDeactivationBlockedDialog from '../../../../components/venue/ItemDeactivationBlockedDialog';
import type { ItemPricingByVenue, MenuLink } from '../../../../services/inventoryItemService';
import menuService from '../../../../services/menuService';
import menuItemService from '../../../../services/menuItemService';
import wineService, { type Wine } from '../../../../services/wineService';
import type { Menu, MenuVisibility } from '../../../../types/menu';
import inventoryItemRecipeService from '../../../../services/inventoryItemRecipeService';
import type { InventoryItemRecipe } from '../../../../types/execution';
import InventoryRecipeEditor from '../../../../components/venue/InventoryRecipeEditor';
import inventoryMovementService from '../../../../services/inventoryMovementService';
import inventoryBalanceService, { type InventoryBalanceAggregate } from '../../../../services/inventoryBalanceService';
import inventoryReservationService, { type InventoryItemReservation } from '../../../../services/inventoryReservationService';
import inventoryLotService from '../../../../services/inventoryLotService';
import type { ItemLotsResponse } from '../../../../types/venue';
import LotsHelpModal from '../../../../components/venue/LotsHelpModal';
import venueLocationService, { pickDefaultLocation } from '../../../../services/venueLocationService';
import venueSupplierService from '../../../../services/venueSupplierService';
import * as supplierItemLinkService from '../../../../services/supplierItemLinkService';
import type { SupplierItemLink } from '../../../../services/supplierItemLinkService';
import InventoryQuantityBadge from '../../../../components/venue/InventoryQuantityBadge';
import { formatQty } from '../../../../utils/quantityFormat';
import { formatSourceRef } from '../../../../utils/sourceRef';
import MovementTypeBadge from '../../../../components/venue/MovementTypeBadge';
import type { InventoryItem, InventoryMovement, Supplier, VenueLocation } from '../../../../types/venue';
import { fromMinorUnitsOrNull, costTimesQty } from '../../../../utils/money';
import { humanizeItemType, itemTypeColor } from '../../../../utils/inventoryItemDisplay';
import { useAuth } from '../../../../context/AuthContext';
import ItemCostSection from '../../../../components/venue/ItemCostSection';

const InventoryItemDetail: React.FC = () => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const { venueId, defaultCurrency } = useVenue();
  const { itemId } = useParams<{ itemId: string }>();
  const { canViewStockCosts, isAdmin } = useAuth();

  // SIMPLE-WINE.4 §7 — the status line under the item name is unchanged for
  // everyone; for an admin it opens the link dialog. The remedy comes to the
  // click rather than the reader travelling to a panel further down.
  const [wineLinkOpen, setWineLinkOpen] = useState(false);



  const backPath = `/admin/venues/${venueId}/inventory`;

  // ── Item ──────────────────────────────────────────────────────────────────────
  const [item, setItem] = useState<InventoryItem | null>(null);
  const [itemLoading, setItemLoading] = useState(true);
  const [itemError, setItemError] = useState<string | null>(null);

  // ── QR / Short ID (INVENTORY-SHORTID-VISIBILITY.1) ────────────────────────────
  const [copiedMsg, setCopiedMsg] = useState<string | null>(null);
  const [qrOpen, setQrOpen] = useState(false);
  const copyText = (text: string, label: string) => {
    navigator.clipboard?.writeText(text).then(
      () => setCopiedMsg(`${label} copied`),
      () => setCopiedMsg('Copy failed — select and copy manually'),
    );
  };
  const qrUrl = item?.shortId ? `${window.location.origin}/i/${item.shortId}` : '';

  // INVENTORY-QR-DOWNLOAD.1 — QR PNG for the public inventory URL (qrUrl only;
  // never the Mongo itemId). Rendered client-side once the dialog opens.
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!qrOpen || !qrUrl) { setQrDataUrl(null); return; }
    let cancelled = false;
    QRCode.toDataURL(qrUrl, { width: 512, margin: 1, errorCorrectionLevel: 'M' })
      .then(url => { if (!cancelled) setQrDataUrl(url); })
      .catch(() => { if (!cancelled) setQrDataUrl(null); });
    return () => { cancelled = true; };
  }, [qrOpen, qrUrl]);

  const downloadQr = () => {
    if (!qrDataUrl || !item?.shortId) return;
    const a = document.createElement('a');
    a.href = qrDataUrl;
    a.download = `inventory-qr-${item.shortId}.png`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  // ── Balance aggregate ─────────────────────────────────────────────────────────
  const [balanceAggregate, setBalanceAggregate] = useState<InventoryBalanceAggregate | null>(null);
  const [balanceLoading, setBalanceLoading] = useState(true);

  // ── Locations (loaded once on mount for location breakdown + recompute dialog) ─
  const [locations, setLocations] = useState<VenueLocation[]>([]);

  useEffect(() => {
    if (!venueId) return;
    venueLocationService.getLocations(venueId)
      .then(locs => setLocations(locs.filter(l => l.active)))
      .catch(() => setLocations([]));
  }, [venueId]);

  // ── Lots (lot-tracked items only) ─────────────────────────────────────────────
  const [lotsExpanded, setLotsExpanded] = useState(true);
  const [lotsHelpOpen, setLotsHelpOpen] = useState(false);
  const [lotsResponse, setLotsResponse] = useState<ItemLotsResponse | null>(null);
  const [lotsLoading, setLotsLoading] = useState(false);
  const [lotsError, setLotsError] = useState<string | null>(null);

  // ── Delete ────────────────────────────────────────────────────────────────────
  /**
   * ITEM-ACTIVE.2 — the refusal, when it happens, is a place to go rather than
   * a message to read. Holds the quantity the server reported.
   */
  const [blockedQty, setBlockedQty] = useState<number | null>(null);
  const [blockedOpen, setBlockedOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // ── Opening stock (INVENTORY-TRUST.1a) ───────────────────────────────────────
  const [openingStockOpen, setOpeningStockOpen] = useState(false);
  const handleOpeningStockSuccess = () => {
    setCopiedMsg('Opening stock set — starting balance recorded.');
    if (venueId && itemId) {
      inventoryBalanceService.getItemBalances(venueId, itemId)
        .then(setBalanceAggregate)
        .catch(() => { /* non-blocking */ });
    }
  };

  // ── Recompute balance ─────────────────────────────────────────────────────────
  const [recomputeOpen, setRecomputeOpen] = useState(false);
  const [recomputing, setRecomputing] = useState(false);
  const [recomputeResult, setRecomputeResult] = useState<{ before: number; after: number; difference: number } | null>(null);
  const [recomputeError, setRecomputeError] = useState<string | null>(null);
  const [recomputeLocationId, setRecomputeLocationId] = useState('');

  useEffect(() => {
    if (!recomputeOpen) return;
    const def = pickDefaultLocation(locations);
    setRecomputeLocationId(def?._id ?? '');
    setRecomputeResult(null);
    setRecomputeError(null);
  }, [recomputeOpen, locations]);

  const handleRecompute = async () => {
    if (!venueId || !itemId || !recomputeLocationId) return;
    setRecomputing(true);
    setRecomputeError(null);
    try {
      const result = await inventoryBalanceService.recomputeBalance(venueId, itemId, recomputeLocationId);
      setRecomputeResult(result);
      inventoryBalanceService.getItemBalances(venueId, itemId)
        .then(setBalanceAggregate)
        .catch(() => { /* non-blocking */ });
    } catch (err: any) {
      setRecomputeError(err?.response?.data?.error?.message ?? 'Failed to recompute balance.');
    } finally {
      setRecomputing(false);
    }
  };

  /**
   * ITEM-ACTIVE.1 — one handler for both directions.
   *
   * Deactivating used to be spelled "delete" and navigated away, which is why
   * there was never a route back: the item vanished from the list it was
   * filtered out of, and no screen offered to bring it back. Now the page
   * stays put and simply re-reads the item, so the state that changed is
   * visible on the screen that changed it.
   */
  const { offer: offerUndo, toast: undoToast } = useRetireUndo({
    venueId,
    onUndone: async () => { setItem(prev => (prev ? { ...prev, active: !(prev.active !== false) } : prev)); },
    onError: setDeleteError,
  });

  const handleSetActive = async (next: boolean) => {
    if (!venueId || !itemId) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      const updated = await inventoryItemService.setActive(venueId, itemId, next);
      setItem(prev => (prev ? { ...prev, ...updated, active: next } : prev));
      offerUndo({
        message: `${next ? 'Reactivated' : 'Retired'} ${item?.displayName ?? 'item'}`,
        itemIds: [itemId],
        restoreTo: !next,
      });
    } catch (err) {
      // 409 either way: stock on hand going down, a name collision coming back.
      // ITEM-ACTIVE.2 — the stock case is answerable, so it gets the guided
      // dialog instead of a red line the user can only read and obey. The name
      // collision stays a message: nothing on this screen can resolve it.
      const detail = extractInventoryErrorDetail(err);
      if (detail.code === 'ITEM_HAS_STOCK') {
        setBlockedQty(detail.quantity);
        setBlockedOpen(true);
      } else {
        setDeleteError(detail.message);
      }
    } finally {
      setDeleting(false);
    }
  };

  // ── Menu memberships (new Menu system, read-only) ──────────────────────────────
  const [menuLinks, setMenuLinks] = useState<MenuLink[]>([]);
  const [menuLinksLoading, setMenuLinksLoading] = useState(false);

  const loadMenuLinks = useCallback(async () => {
    if (!venueId || !itemId) return;
    setMenuLinksLoading(true);
    try {
      const links = await inventoryItemService.getMenuLinks(venueId, itemId);
      setMenuLinks(links);
    } catch { /* non-blocking */ }
    finally { setMenuLinksLoading(false); }
  }, [venueId, itemId]);

  useEffect(() => { loadMenuLinks(); }, [loadMenuLinks]);

  // ── Add to menu (INVENTORY-MENU-PLACEMENT.2) ────────────────────────────────────
  // Places this wine-linked item on a Menu as a kind=WINE MenuItem — the new
  // placement model. Never writes a legacy WineListItem. Menus and the wine's
  // display data are loaded lazily when the dialog opens.
  const [addToMenuOpen, setAddToMenuOpen] = useState(false);
  const [menus, setMenus] = useState<Menu[]>([]);
  const [menusLoading, setMenusLoading] = useState(false);
  const [selectedMenuId, setSelectedMenuId] = useState('');
  const [menuItemVisibility, setMenuItemVisibility] = useState<MenuVisibility>('LIVE');
  const [menuWine, setMenuWine] = useState<Wine | null>(null);
  const [addingToMenu, setAddingToMenu] = useState(false);
  const [addToMenuError, setAddToMenuError] = useState('');

  const openAddToMenu = async () => {
    if (!venueId || !item?.wineId) return;
    setAddToMenuOpen(true);
    setAddToMenuError('');
    setMenusLoading(true);
    try {
      const [menusResult, wine] = await Promise.all([
        menuService.getMenus(venueId, 1, 100, undefined, 'all'),
        wineService.getById(item.wineId),
      ]);
      const activeMenus = menusResult.data.filter(m => m.status !== 'ARCHIVED');
      setMenus(activeMenus);
      setSelectedMenuId(activeMenus.length > 0 ? activeMenus[0]._id : '');
      setMenuWine(wine);
    } catch {
      setMenus([]);
      setMenuWine(null);
      setAddToMenuError('Failed to load menus. Please try again.');
    } finally {
      setMenusLoading(false);
    }
  };

  const handleAddToMenu = async () => {
    if (!venueId || !item?.wineId || !selectedMenuId || !menuWine) return;
    setAddingToMenu(true);
    setAddToMenuError('');
    try {
      await menuItemService.createItem(venueId, selectedMenuId, {
        kind: 'WINE',
        displaySnapshot: { name: menuWine.name },
        sectionId: null,
        position: 0,
        visibility: menuItemVisibility,
        source: 'MANUAL',
        needsReview: false,
        inventoryItemId: item._id,
        ...(defaultCurrency && { currency: defaultCurrency }),
        wineDetails: {
          wineId: item.wineId,
          // Backend requires all 5 wineDisplay fields as strings; empty string
          // is accepted for unknown values.
          wineDisplay: {
            name: menuWine.name,
            displayVintage: menuWine.displayVintage ?? '',
            producerName: menuWine.wineName?.producer?.name ?? '',
            regionName: menuWine.wineName?.region?.name ?? '',
            color: menuWine.wineName?.color ?? '',
          },
          ...(item.sizeValue != null && { bottleSize: item.sizeValue }),
        },
      } as Parameters<typeof menuItemService.createItem>[2]);
      setAddToMenuOpen(false);
      setCopiedMsg('Added to menu');
      loadMenuLinks();
    } catch (err: any) {
      const status = err?.response?.status;
      const msg = err?.response?.data?.message ?? '';
      if (status === 409 || msg.toLowerCase().includes('exists') || msg.toLowerCase().includes('duplicate')) {
        setAddToMenuError('This wine is already on the selected menu.');
      } else {
        setAddToMenuError('Failed to add to menu. Please try again.');
      }
    } finally {
      setAddingToMenu(false);
    }
  };

  // ── POS soft reservations (RESERVATION-VISIBILITY.1a, read-only) ───────────────
  const [reservations, setReservations] = useState<InventoryItemReservation[]>([]);
  const [reservationsLoading, setReservationsLoading] = useState(false);

  const loadReservations = useCallback(async () => {
    if (!venueId || !itemId) return;
    setReservationsLoading(true);
    try {
      const res = await inventoryReservationService.getItemReservations(venueId, itemId);
      setReservations(res.reservations ?? []);
    } catch { /* non-blocking */ }
    finally { setReservationsLoading(false); }
  }, [venueId, itemId]);

  useEffect(() => { loadReservations(); }, [loadReservations]);

  // ── Sale-time recipe (InventoryItemRecipe) — only for RECIPE_CONSUMPTION ────
  // MENU-EXEC.4C — surface the active recipe on recipe-backed items. Read via
  // the existing GET endpoint; edited inline via the existing item-level editor.
  const [recipe, setRecipe] = useState<InventoryItemRecipe | null>(null);
  const [recipeLoading, setRecipeLoading] = useState(false);
  const [recipeEditing, setRecipeEditing] = useState(false);

  const loadRecipe = useCallback(async () => {
    if (!venueId || !itemId) return;
    setRecipeLoading(true);
    try {
      const r = await inventoryItemRecipeService.getItemRecipe(venueId, itemId);
      setRecipe(r);
    } catch { /* non-blocking */ }
    finally { setRecipeLoading(false); }
  }, [venueId, itemId]);

  useEffect(() => {
    if (item?.executionBehavior === 'RECIPE_CONSUMPTION') loadRecipe();
  }, [item?.executionBehavior, loadRecipe]);

  // ── Supplier links ────────────────────────────────────────────────────────────
  const [supplierLinks, setSupplierLinks] = useState<SupplierItemLink[]>([]);
  const [supplierLinksLoading, setSupplierLinksLoading] = useState(false);
  const [supplierLinksError, setSupplierLinksError] = useState<string | null>(null);

  // Add supplier dialog
  const [addSupplierOpen, setAddSupplierOpen] = useState(false);
  const [availableSuppliers, setAvailableSuppliers] = useState<Supplier[]>([]);
  const [suppliersLoading, setSuppliersLoading] = useState(false);
  const [newSupplierId, setNewSupplierId] = useState('');
  const [newSupplierRef, setNewSupplierRef] = useState('');
  const [newSupplierCode, setNewSupplierCode] = useState('');
  const [addSupplierSaving, setAddSupplierSaving] = useState(false);
  const [addSupplierError, setAddSupplierError] = useState('');

  // Edit reference dialog
  const [editRefOpen, setEditRefOpen] = useState(false);
  const [editRefLink, setEditRefLink] = useState<SupplierItemLink | null>(null);
  const [editRefValue, setEditRefValue] = useState('');
  const [editCodeValue, setEditCodeValue] = useState('');
  const [editRefSaving, setEditRefSaving] = useState(false);
  const [editRefError, setEditRefError] = useState('');

  const loadSupplierLinks = useCallback(async () => {
    if (!venueId || !itemId) return;
    setSupplierLinksLoading(true);
    setSupplierLinksError(null);
    try {
      const links = await supplierItemLinkService.getLinks(venueId, itemId);
      setSupplierLinks(links);
    } catch {
      setSupplierLinksError('Failed to load supplier links.');
    } finally {
      setSupplierLinksLoading(false);
    }
  }, [venueId, itemId]);

  useEffect(() => { loadSupplierLinks(); }, [loadSupplierLinks]);

  /**
   * ITEM-PRICING-BY-VENUE.1 — what the sister venues charge.
   *
   * Loaded on mount, and the panel renders only when the company has more than
   * one venue. Failure is SILENT: this is a supplementary read, and the item's
   * own page must not break because a cross-venue lookup did.
   */
  const [pricing, setPricing] = useState<ItemPricingByVenue | null>(null);
  const loadPricing = useCallback(async () => {
    if (!venueId || !itemId) return;
    try {
      setPricing(await inventoryItemService.getItemPricing(venueId, itemId));
    } catch {
      setPricing(null);
    }
  }, [venueId, itemId]);

  useEffect(() => { loadPricing(); }, [loadPricing]);

  const openAddSupplierDialog = async () => {
    setNewSupplierId('');
    setNewSupplierRef('');
    setNewSupplierCode('');
    setAddSupplierError('');
    setAddSupplierOpen(true);
    setSuppliersLoading(true);
    try {
      // Venue-scoped — picker shows only suppliers connected to this venue.
      // To onboard a brand-new supplier, an admin must first link/PO/invoice it.
      if (!venueId) {
        setAvailableSuppliers([]);
        return;
      }
      const result = await venueSupplierService.listVenueSuppliers(venueId, 1, 200);
      setAvailableSuppliers(result.data);
    } catch {
      setAvailableSuppliers([]);
    } finally {
      setSuppliersLoading(false);
    }
  };

  const handleAddSupplier = async () => {
    if (!venueId || !itemId || !newSupplierId) return;
    setAddSupplierSaving(true);
    setAddSupplierError('');
    try {
      await supplierItemLinkService.createLink(venueId, itemId, {
        supplierId: newSupplierId,
        supplierReference: newSupplierRef.trim() || undefined,
        supplierItemCode: newSupplierCode.trim() || undefined,
      });
      setAddSupplierOpen(false);
      loadSupplierLinks();
    } catch (err: any) {
      const code = err?.response?.data?.error?.code;
      setAddSupplierError(
        code === 'SUPPLIER_ITEM_LINK_DUPLICATE'
          ? 'This supplier is already linked to this item.'
          : 'Failed to add supplier. Please try again.',
      );
    } finally {
      setAddSupplierSaving(false);
    }
  };

  const openEditRef = (link: SupplierItemLink) => {
    setEditRefLink(link);
    setEditRefValue(link.supplierReference ?? '');
    setEditCodeValue(link.supplierItemCode ?? '');
    setEditRefError('');
    setEditRefOpen(true);
  };

  const handleEditRef = async () => {
    if (!venueId || !itemId || !editRefLink) return;
    setEditRefSaving(true);
    setEditRefError('');
    try {
      await supplierItemLinkService.updateLink(venueId, itemId, editRefLink._id, {
        supplierReference: editRefValue.trim() || undefined,
        supplierItemCode: editCodeValue.trim() || null,
      });
      setEditRefOpen(false);
      loadSupplierLinks();
    } catch {
      setEditRefError('Failed to update reference. Please try again.');
    } finally {
      setEditRefSaving(false);
    }
  };

  const handleTogglePreferred = async (link: SupplierItemLink) => {
    if (!venueId || !itemId) return;
    try {
      if (link.isPreferred) {
        await supplierItemLinkService.clearPreferred(venueId, itemId, link._id);
      } else {
        await supplierItemLinkService.setPreferred(venueId, itemId, link._id);
      }
      loadSupplierLinks();
    } catch {
      setSupplierLinksError('Failed to update preferred supplier.');
    }
  };

  const handleToggleActive = async (link: SupplierItemLink, active: boolean) => {
    if (!venueId || !itemId) return;
    try {
      await supplierItemLinkService.updateLink(venueId, itemId, link._id, { active });
      loadSupplierLinks();
    } catch {
      setSupplierLinksError('Failed to update supplier link.');
    }
  };

  // ── Movements ─────────────────────────────────────────────────────────────────
  const [movements, setMovements] = useState<InventoryMovement[]>([]);
  const [movTotal, setMovTotal] = useState(0);
  const [movPage, setMovPage] = useState(0); // 0-based for MUI
  const [movPageSize, setMovPageSize] = useState(25);
  const [movLoading, setMovLoading] = useState(false);
  const [movError, setMovError] = useState<string | null>(null);

  // ── Load item + balance ───────────────────────────────────────────────────────
  useEffect(() => {
    if (!venueId || !itemId) return;
    setItemLoading(true);
    setItemError(null);

    inventoryItemService.getItem(venueId, itemId)
      .then(data => {
        setItem(data);
        setBalanceLoading(true);
        inventoryBalanceService.getItemBalances(venueId, itemId)
          .then(setBalanceAggregate)
          .catch(() => { /* non-blocking — balanceLoading=false triggers empty state */ })
          .finally(() => setBalanceLoading(false));
      })
      .catch(err => setItemError(extractInventoryError(err)))
      .finally(() => setItemLoading(false));
  }, [venueId, itemId]);

  // ── Load movements ────────────────────────────────────────────────────────────
  const loadMovements = useCallback(async () => {
    if (!venueId || !itemId) return;
    setMovLoading(true);
    setMovError(null);
    try {
      const result = await inventoryMovementService.getMovements(
        venueId,
        movPage + 1,
        movPageSize,
        itemId,
      );
      setMovements(result.data ?? []);
      setMovTotal(result.pagination?.totalItems ?? 0);
    } catch (err) {
      setMovError(extractInventoryError(err));
    } finally {
      setMovLoading(false);
    }
  }, [venueId, itemId, movPage, movPageSize]);

  useEffect(() => { loadMovements(); }, [loadMovements]);

  // ── Load lots (only when item is lot-tracked) ─────────────────────────────────
  useEffect(() => {
    if (!venueId || !itemId || !item?.lotTracked) return;
    setLotsLoading(true);
    setLotsError(null);
    inventoryLotService.getItemLots(venueId, itemId)
      .then(setLotsResponse)
      .catch(() => setLotsError('Failed to load lots.'))
      .finally(() => setLotsLoading(false));
  }, [venueId, itemId, item]);

  // ── Helpers ───────────────────────────────────────────────────────────────────
  const fmt = (n?: number | null) =>
    n != null
      ? `${defaultCurrency ? `${defaultCurrency} ` : ''}${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
      : '—';

  const fmtDate = (iso: string) =>
    new Date(iso).toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' });

  const qtyColor = (qty: number) => (qty > 0 ? '#1a7f37' : qty < 0 ? '#c0392b' : 'text.secondary');

  const resolveLocationName = (locationId: string) =>
    locations.find(l => l._id === locationId)?.displayName ?? locationId;

  // ── Loading state ─────────────────────────────────────────────────────────────
  if (itemLoading) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center', pt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (itemError || !item) {
    return (
      <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Button startIcon={<BackIcon />} onClick={() => navigate(backPath)} sx={{ textTransform: 'none', alignSelf: 'flex-start', color: theme.palette.text.secondary }}>
          Inventory
        </Button>
        <Alert severity="error">{itemError ?? 'Item not found.'}</Alert>
      </Box>
    );
  }

  const adjustPath = `/admin/venues/${venueId}/inventory/adjust?itemId=${itemId}`;

  // RECIPE-ITEM-UI.1 — recipe-backed items are not stock-bearing; hide stock-only
  // UI (quantity/cost/lots/suppliers/movements/adjust) and keep the recipe-focused
  // surface (identity, selling price, ingredients, menus).
  const isRecipe = item.executionBehavior === 'RECIPE_CONSUMPTION';

  return (
    <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
      {/* Back + actions */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
        <Button
          startIcon={<BackIcon />}
          onClick={() => navigate(backPath)}
          sx={{ textTransform: 'none', color: theme.palette.text.secondary }}
        >
          Inventory
        </Button>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {!isRecipe && (
            <Tooltip title="Post manual adjustment">
              <Button
                variant="contained"
                startIcon={<AdjustIcon />}
                onClick={() => navigate(adjustPath)}
                sx={{ textTransform: 'none', fontWeight: 600 }}
              >
                Adjust
              </Button>
            </Tooltip>
          )}
          <Tooltip title="Edit configuration">
            <IconButton onClick={() => navigate(`/admin/venues/${venueId}/inventory/${itemId}/edit`)}>
              <EditIcon />
            </IconButton>
          </Tooltip>
          {!isRecipe && (
            <Tooltip title="Recompute stock balance">
              <IconButton onClick={() => setRecomputeOpen(true)}>
                <RecalcIcon />
              </IconButton>
            </Tooltip>
          )}
          {/* ITEM-ACTIVE.3 — the status control moved into the header row, next
              to the wine-link line, and the action bar lost both of its
              affordances. There is now ONE place that both reports the state
              and changes it, in either direction. */}
        </Box>
      </Box>

      {/* Header card */}
      <Paper sx={{ p: 3, borderRadius: 2, boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
        <Typography variant="h6" fontWeight={600}>
          {item.displayName}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5, mb: 2 }}>
          {/* ITEM-ACTIVE.3 — shown in BOTH states, so "is this live?" is
              answered by looking rather than by inferring from what is
              missing. Inactive keeps the exact appearance it had in .1. */}
          <ItemStatusChip
            active={item.active !== false}
            canEdit={isAdmin()}
            busy={deleting}
            onDeactivate={() => handleSetActive(false)}
            onReactivate={() => handleSetActive(true)}
          />
          {/* MENU-EXEC.4D — fulfillment wins for recipe-backed items: show a
              single "Made from a recipe" chip and suppress the category chip
              (the item is stored as PURCHASED but isn't purchased stock).
              Direct-stock items keep their category chip. */}
          {item.executionBehavior === 'RECIPE_CONSUMPTION' ? (
            <Chip
              label="Made from a recipe"
              size="small"
              sx={{
                fontSize: '0.68rem',
                height: 20,
                fontWeight: 600,
                color: '#9c27b0',
                backgroundColor: 'rgba(156,39,176,0.08)',
                border: 'none',
              }}
            />
          ) : (
            <Chip
              label={`${humanizeItemType(item.type)} item`}
              size="small"
              sx={{
                fontSize: '0.68rem',
                height: 20,
                fontWeight: 600,
                color: itemTypeColor(item.type) ?? 'text.secondary',
                backgroundColor: itemTypeColor(item.type)
                  ? alpha(itemTypeColor(item.type) as string, 0.08)
                  : 'rgba(0,0,0,0.06)',
                border: 'none',
              }}
            />
          )}
          {item.type === 'WINE' && item.executionBehavior !== 'RECIPE_CONSUMPTION' && (
            <WineLinkStatusChip
              linked={Boolean(item.wineId)}
              isAdmin={isAdmin()}
              onOpenLink={() => setWineLinkOpen(true)}
            />
          )}
          {/* Public QR page (INVENTORY-SHORTID-VISIBILITY.1) — subtle chip that
              opens a small dialog with the short id + shareable /i/:shortId link. */}
          {item.shortId && (
            <Chip
              icon={<QrIcon sx={{ fontSize: 15 }} />}
              label="Public QR Page"
              size="small"
              clickable
              onClick={() => setQrOpen(true)}
              sx={{
                fontSize: '0.68rem',
                height: 20,
                fontWeight: 600,
                color: '#0e7490',
                backgroundColor: 'rgba(14,116,144,0.08)',
                border: 'none',
                '& .MuiChip-icon': { color: '#0e7490', ml: 0.5 },
              }}
            />
          )}
        </Box>

        <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
          {/* On hand (RESERVATION-VISIBILITY.1a — was "Total Stock") */}
          {!isRecipe && (
            <Box>
              <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                On Hand
              </Typography>
              <Box sx={{ mt: 0.5 }}>
                {balanceLoading ? (
                  <CircularProgress size={18} />
                ) : (
                  <InventoryQuantityBadge
                    quantity={balanceAggregate?.totalQuantity ?? 0}
                    parLevel={item.parLevel}
                    reorderPoint={item.reorderPoint}
                    size="medium"
                  />
                )}
              </Box>
            </Box>
          )}

          {/* Reserved — POS soft holds (RESERVATION-VISIBILITY.1a) */}
          {!isRecipe && (
            <Box>
              <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                Reserved
              </Typography>
              <Typography
                variant="body1"
                fontWeight={600}
                sx={{ mt: 0.5, color: (balanceAggregate?.totalReserved ?? 0) > 0 ? '#b45309' : 'text.primary' }}
              >
                {formatQty(balanceAggregate?.totalReserved ?? 0)}
              </Typography>
              <Typography variant="caption" color="text.secondary">Held by open POS orders</Typography>
            </Box>
          )}

          {/* Available = On hand − Reserved (may be negative → oversold) */}
          {!isRecipe && (
            <Box>
              <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                Available
              </Typography>
              <Typography
                variant="body1"
                fontWeight={600}
                sx={{
                  mt: 0.5,
                  color: (balanceAggregate?.totalAvailable ?? balanceAggregate?.totalQuantity ?? 0) < 0
                    ? theme.palette.error.main
                    : 'text.primary',
                }}
              >
                {formatQty(balanceAggregate?.totalAvailable ?? balanceAggregate?.totalQuantity ?? 0)}
              </Typography>
              <Typography variant="caption" color="text.secondary">On hand − reserved</Typography>
            </Box>
          )}

          {/* Last cost — taken from most recently updated balance location that has a cost */}
          {!isRecipe && (
            <Box>
              <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                Last Cost
              </Typography>
              <Typography variant="body1" fontWeight={600} sx={{ mt: 0.5 }}>
                {fmt(fromMinorUnitsOrNull(
                  balanceAggregate?.locationBreakdown
                    .filter(e => e.lastCost != null)
                    .sort((a, b) => new Date(b.lastUpdated).getTime() - new Date(a.lastUpdated).getTime())[0]
                    ?.lastCost
                ))}
              </Typography>
              <Typography variant="caption" color="text.secondary">Most recent receipt cost</Typography>
            </Box>
          )}

          {/* Par level */}
          {!isRecipe && item.parLevel != null && (
            <Box>
              <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                Par Level
              </Typography>
              <Typography variant="body1" fontWeight={600} sx={{ mt: 0.5 }}>{item.parLevel}</Typography>
            </Box>
          )}

          {/* Reorder / production point */}
          {!isRecipe && item.reorderPoint != null && (
            <Box>
              <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                {item.type === 'MANUFACTURED' ? 'Produce At' : 'Reorder At'}
              </Typography>
              <Typography variant="body1" fontWeight={600} sx={{ mt: 0.5 }}>{item.reorderPoint}</Typography>
            </Box>
          )}

          {/* Size */}
          {!isRecipe && item.sizeValue != null && (
            <Box>
              <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                Size
              </Typography>
              <Typography variant="body1" fontWeight={600} sx={{ mt: 0.5 }}>
                {item.sizeValue} {item.sizeUnit ?? 'ml'}
              </Typography>
            </Box>
          )}

          {/* Bin location */}
          {!isRecipe && item.binLocation && (
            <Box>
              <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                Bin Location
              </Typography>
              <Typography variant="body1" fontWeight={600} sx={{ mt: 0.5 }}>
                {item.binLocation}
              </Typography>
            </Box>
          )}

          {/* Category — user-definable. */}
          {item.categoryName && (
            <Box>
              <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                Category
              </Typography>
              <Typography variant="body1" fontWeight={600} sx={{ mt: 0.5 }}>
                {item.categoryName}
              </Typography>
              {item.subcategoryName && (
                <Typography variant="caption" color="text.secondary">
                  {item.subcategoryName}
                </Typography>
              )}
            </Box>
          )}

          {/* Source */}
          <Box>
            <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: '0.04em' }}>
              Source
            </Typography>
            <Box sx={{ mt: 0.5 }}>
              <Chip label={item.source} size="small" variant="outlined" sx={{ fontSize: '0.7rem', height: 20 }} />
            </Box>
          </Box>

          {/* ITEM-PRICING-BY-VENUE.1 — this is THIS VENUE's price, not the
              item's. applyItemOverlays rewrote salePrice in place before the
              page ever saw it, so the old "Default Selling Price" label was
              wrong for any item a sister venue prices differently. Stored in
              minor units; shown in major. Distinct from menu/wine-list prices. */}
          <Box>
            <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: '0.04em' }}>
              Selling Price Here
            </Typography>
            <Typography variant="body1" fontWeight={600} sx={{ mt: 0.5 }}>
              {item.salePrice != null
                ? `${(item.salePrice / 100).toFixed(2)} ${item.salePriceCurrency ?? defaultCurrency}`
                : '—'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              What this venue charges when the item is sold directly through POS
            </Typography>
          </Box>
        </Box>
      </Paper>

      {/* ── ITEM-PRICING-BY-VENUE.1 — what the sister venues charge ─────────── */}
      {/* Rendered only for a multi-venue company. On a single-venue install
          there is no second price to compare against, and the panel would be a
          row restating the field above it. */}
      {pricing && pricing.venues.length > 1 && (
        <Paper sx={{ borderRadius: 2, overflow: 'hidden', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
          <Box sx={{ px: 3, py: 2, borderBottom: `1px solid ${alpha(theme.palette.divider, 0.12)}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
            <Box>
              <Typography variant="subtitle1" fontWeight={600}>Pricing by venue</Typography>
              <Typography variant="caption" color="text.secondary">
                What each venue in the company charges for this item.
              </Typography>
            </Box>
            {pricing.summary.pricesDiffer && (
              <Chip label="Prices differ" size="small" color="warning" variant="outlined" sx={{ fontWeight: 600 }} />
            )}
          </Box>
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Venue</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Price</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pricing.venues.map(v => (
                  <TableRow key={v.venueId} sx={v.isCurrentVenue ? { bgcolor: alpha(theme.palette.primary.main, 0.04) } : undefined}>
                    <TableCell>
                      <Typography variant="body2" fontWeight={v.isCurrentVenue ? 700 : 400}>
                        {v.venueName}{v.isCurrentVenue ? ' (here)' : ''}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {/* Four states, not a blank. "Never added here" and "sold
                          here and nobody priced it" need opposite actions, and
                          one empty cell for both is what makes a pricing review
                          untrustworthy. */}
                      <Typography variant="caption" color={v.state === 'NO_PRICE_SET' ? 'warning.main' : 'text.secondary'}>
                        {v.state === 'PRICED' ? 'Priced here'
                          : v.state === 'NO_PRICE_SET' ? 'Sold here — no price set'
                          : v.state === 'WITHDRAWN' ? 'Withdrawn'
                          : 'Not sold here'}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" fontWeight={600} sx={{ fontVariantNumeric: 'tabular-nums' }}>
                        {v.effectiveSalePrice != null
                          ? `${(v.effectiveSalePrice / 100).toFixed(2)} ${v.effectiveSalePriceCurrency ?? defaultCurrency}`
                          : '—'}
                      </Typography>
                      {/* A venue with no row of its own inherits the master. Worth
                          saying: nobody set that number for this venue. */}
                      {v.state === 'NOT_SOLD_HERE' && v.effectiveSalePrice != null && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          from item default
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* MENU-EXEC.4C — Recipe section, only for recipe-backed items. Reads the
          active InventoryItemRecipe via the existing endpoint and edits inline
          via the existing item-level InventoryRecipeEditor. */}
      {item.executionBehavior === 'RECIPE_CONSUMPTION' && (
        <Paper sx={{ borderRadius: 2, overflow: 'hidden', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
          <Box sx={{ px: 3, py: 2, borderBottom: `1px solid ${alpha(theme.palette.divider, 0.12)}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
            <Box>
              <Typography variant="subtitle1" fontWeight={600}>Recipe ingredients</Typography>
              <Typography variant="caption" color="text.secondary">What one serving depletes when sold through POS.</Typography>
            </Box>
            {!recipeEditing && !recipeLoading && (
              <Button
                size="small"
                startIcon={recipe && recipe.components.length > 0 ? <EditIcon /> : <AddIcon />}
                onClick={() => setRecipeEditing(true)}
                sx={{ textTransform: 'none', fontWeight: 500 }}
              >
                {recipe && recipe.components.length > 0 ? 'Edit recipe' : 'Add recipe'}
              </Button>
            )}
          </Box>
          <Box sx={{ p: 3 }}>
            {recipeEditing ? (
              <InventoryRecipeEditor
                venueId={venueId}
                itemId={item._id}
                initialComponents={recipe?.components ?? null}
                onSaved={() => { setRecipeEditing(false); loadRecipe(); }}
                onCancel={() => setRecipeEditing(false)}
              />
            ) : recipeLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress size={22} />
              </Box>
            ) : recipe && recipe.components.length > 0 ? (
              <Box>
                <Box component="ul" sx={{ m: 0, pl: 2.25 }}>
                  {recipe.components.map((c, i) => (
                    <Typography component="li" key={`${c.inventoryItemId}-${i}`} variant="body2" sx={{ display: 'list-item', lineHeight: 1.8 }}>
                      <strong>{c.displayName ?? 'Inventory item'}</strong>
                      {' — '}
                      {c.inputQuantity != null
                        ? `${c.inputQuantity} ${c.inputUnit ?? ''}`.trim()
                        : `${c.quantity} unit${c.quantity === 1 ? '' : 's'}`}
                      {/* RECIPE-ITEM-COST.1 — per-line cost when the backend provides it.
                          lineCost is MINOR units, like every other money field on
                          this page; this was the one render that passed it to fmt
                          raw, so a HKD 70.00 pour read as HKD 7,000.00 beside a
                          total that was already correct. */}
                      {recipe.costSummary && (
                        <Box component="span" sx={{ color: 'text.secondary', ml: 1 }}>
                          · {c.lineCost != null ? fmt(fromMinorUnitsOrNull(c.lineCost)) : 'Cost unavailable'}
                        </Box>
                      )}
                    </Typography>
                  ))}
                </Box>
                {/* RECIPE-ITEM-COST.1 — calculated total (read-only; never blocks editing). */}
                {recipe.costSummary && (
                  <Box
                    sx={{
                      mt: 1.5,
                      pt: 1.5,
                      borderTop: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
                      display: 'flex',
                      alignItems: 'baseline',
                      justifyContent: 'space-between',
                      gap: 2,
                    }}
                  >
                    <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                      Calculated recipe cost
                    </Typography>
                    <Box sx={{ textAlign: 'right' }}>
                      <Typography variant="body1" fontWeight={600}>
                        {recipe.costSummary.totalCost != null ? fmt(fromMinorUnitsOrNull(recipe.costSummary.totalCost)) : 'Cost unavailable'}
                      </Typography>
                      {recipe.costSummary.totalCost != null && !recipe.costSummary.complete && (
                        <Typography variant="caption" color="text.secondary">
                          Based on available ingredient costs
                        </Typography>
                      )}
                    </Box>
                  </Box>
                )}
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No recipe ingredients saved yet.
              </Typography>
            )}
          </Box>
        </Paper>
      )}

      {/* Stock by Location */}
      {!isRecipe && (
      <Paper sx={{ borderRadius: 2, overflow: 'hidden', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
        <Box sx={{ px: 3, py: 2, borderBottom: `1px solid ${alpha(theme.palette.divider, 0.12)}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
          <Box>
            <Typography variant="subtitle1" fontWeight={600}>Stock by Location</Typography>
            <Typography variant="caption" color="text.secondary">On hand / reserved / available per location · On Hand above is the sum of all locations</Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            {!balanceLoading && (
              <Button
                size="small"
                onClick={() => setOpeningStockOpen(true)}
                sx={{ textTransform: 'none', fontWeight: 500, color: 'text.secondary', whiteSpace: 'nowrap' }}
              >
                Set opening stock
              </Button>
            )}
            {!balanceLoading && balanceAggregate && balanceAggregate.totalQuantity > 0 && (
              <Button
                size="small"
                onClick={() => navigate(`/admin/venues/${venueId}/ownership-reports`)}
                sx={{ textTransform: 'none', fontWeight: 500, color: 'text.secondary', whiteSpace: 'nowrap' }}
              >
                View by owner →
              </Button>
            )}
          </Box>
        </Box>
        {balanceLoading ? (
          <Box sx={{ py: 3, display: 'flex', justifyContent: 'center' }}>
            <CircularProgress size={24} />
          </Box>
        ) : !balanceAggregate || balanceAggregate.locationBreakdown.length === 0 ? (
          <Box sx={{ py: 4, textAlign: 'center', color: theme.palette.text.secondary }}>
            <Typography variant="body2" fontWeight={500}>No stock yet</Typography>
            <Typography variant="caption" color="text.disabled" sx={{ mt: 0.5, display: 'block' }}>
              Already have bottles on the shelf? Declare them as the starting balance.
            </Typography>
            <Button
              variant="contained"
              size="small"
              onClick={() => setOpeningStockOpen(true)}
              sx={{ mt: 1.5, textTransform: 'none', fontWeight: 600 }}
            >
              Set opening stock
            </Button>
            <Typography variant="caption" color="text.disabled" sx={{ mt: 1, display: 'block' }}>
              New deliveries should be received via Purchasing instead.
            </Typography>
          </Box>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                {['Location', 'On Hand', 'Reserved', 'Available', 'Last Movement', 'Last Updated'].map(h => (
                  <TableCell key={h} sx={{ fontWeight: 600, color: theme.palette.text.secondary, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {balanceAggregate.locationBreakdown.map(entry => (
                <TableRow key={entry.locationId ?? 'unknown'} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight={500}>
                      {entry.locationId ? resolveLocationName(entry.locationId) : '—'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight={700} sx={{ color: qtyColor(entry.quantity) }}>
                      {formatQty(entry.quantity)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ color: (entry.reserved ?? 0) > 0 ? '#b45309' : 'text.secondary' }}>
                      {formatQty(entry.reserved ?? 0)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {(() => {
                      const avail = entry.available ?? (entry.quantity - (entry.reserved ?? 0));
                      return (
                        <Typography variant="body2" fontWeight={600} sx={{ color: avail < 0 ? theme.palette.error.main : 'text.primary' }}>
                          {formatQty(avail)}
                        </Typography>
                      );
                    })()}
                  </TableCell>
                  <TableCell>
                    {entry.lastMovementType
                      ? <MovementTypeBadge type={entry.lastMovementType as any} />
                      : <Typography variant="body2" color="text.disabled">—</Typography>}
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" color="text.secondary">
                      {entry.lastUpdated ? fmtDate(entry.lastUpdated) : '—'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Paper>
      )}

      {/* Open POS Reservations (RESERVATION-VISIBILITY.1a — soft holds, not movements) */}
      {!isRecipe && (
      <Paper sx={{ borderRadius: 2, overflow: 'hidden', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
        <Box sx={{ px: 3, py: 2, borderBottom: `1px solid ${alpha(theme.palette.divider, 0.12)}` }}>
          <Typography variant="subtitle1" fontWeight={600}>Open POS Reservations</Typography>
          <Typography variant="caption" color="text.secondary">
            Soft holds from open POS orders — not stock movements. Released automatically on payment or void.
          </Typography>
        </Box>
        {reservationsLoading ? (
          <Box sx={{ py: 3, display: 'flex', justifyContent: 'center' }}>
            <CircularProgress size={24} />
          </Box>
        ) : reservations.length === 0 ? (
          <Box sx={{ py: 3, textAlign: 'center', color: theme.palette.text.secondary }}>
            <Typography variant="body2">No open reservations.</Typography>
          </Box>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                {['Location', 'Qty', 'POS Order', 'Line', 'Created', 'Status'].map(h => (
                  <TableCell key={h} sx={{ fontWeight: 600, color: theme.palette.text.secondary, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {reservations.map(r => (
                <TableRow key={r.id} hover>
                  <TableCell>
                    <Typography variant="body2">{resolveLocationName(r.locationId)}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600} sx={{ color: '#b45309' }}>{formatQty(r.quantity)}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'monospace', fontSize: '0.78rem' }}>
                      {r.sourceOrderRef}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'monospace', fontSize: '0.78rem' }}>
                      {r.sourceLineRef}{r.componentLineIndex != null ? ` · comp #${r.componentLineIndex}` : ''}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">{fmtDate(r.createdAt)}</Typography>
                  </TableCell>
                  <TableCell>
                    <Chip label={r.status} size="small" color="warning" variant="outlined" sx={{ fontSize: '0.68rem', height: 20 }} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Paper>
      )}

      {/* Lots — only for lot-tracked items */}
      {item.lotTracked && (() => {
        const namedLotTotal = lotsResponse?.lots.reduce(
          (sum, lot) => sum + lot.locations.reduce((s, l) => s + l.quantity, 0), 0
        ) ?? 0;
        const nullLotQty = Math.max(0, (balanceAggregate?.totalQuantity ?? 0) - namedLotTotal);

        return (
          <Paper sx={{ borderRadius: 2, overflow: 'hidden', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
            <Box
              onClick={() => setLotsExpanded(prev => !prev)}
              sx={{
                px: 3,
                py: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                borderBottom: lotsExpanded ? `1px solid ${alpha(theme.palette.divider, 0.12)}` : 'none',
                cursor: 'pointer',
                userSelect: 'none',
                '&:hover': { backgroundColor: alpha(theme.palette.action.hover, 0.5) },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box>
                  <Typography variant="subtitle1" fontWeight={600}>Lots</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Lot / batch records for this item
                  </Typography>
                </Box>
                <Tooltip title="How lot tracking works" placement="right">
                  <IconButton
                    size="small"
                    onClick={e => { e.stopPropagation(); setLotsHelpOpen(true); }}
                    sx={{
                      color: theme.palette.text.disabled,
                      '&:hover': { color: theme.palette.primary.main },
                      ml: 0.25,
                    }}
                  >
                    <HelpIcon sx={{ fontSize: 17 }} />
                  </IconButton>
                </Tooltip>
              </Box>
              <ChevronDownIcon
                sx={{
                  color: theme.palette.text.secondary,
                  transform: lotsExpanded ? 'rotate(180deg)' : 'none',
                  transition: 'transform 0.2s',
                }}
              />
            </Box>

            <Collapse in={lotsExpanded}>
              {/* Null-lot bucket note */}
              {!lotsLoading && !lotsError && nullLotQty > 0 && (
                <Box sx={{ px: 3, py: 1.5, borderBottom: `1px solid ${alpha(theme.palette.divider, 0.08)}` }}>
                  <Alert severity="info" icon={false} sx={{ py: 0.5 }}>
                    <Typography variant="caption">
                      {nullLotQty} unit{nullLotQty !== 1 ? 's' : ''} in unassigned stock. This is stock without a lot.
                    </Typography>
                  </Alert>
                </Box>
              )}

              {lotsLoading ? (
                <Box sx={{ py: 3, display: 'flex', justifyContent: 'center' }}>
                  <CircularProgress size={24} />
                </Box>
              ) : lotsError ? (
                <Box sx={{ px: 3, py: 2 }}>
                  <Alert severity="warning">{lotsError}</Alert>
                </Box>
              ) : !lotsResponse || lotsResponse.lots.length === 0 ? (
                <Box sx={{ py: 4, textAlign: 'center', color: theme.palette.text.secondary }}>
                  <Typography variant="body2" fontWeight={500}>No lots received yet.</Typography>
                  <Typography variant="caption" color="text.disabled" sx={{ mt: 0.5, display: 'block' }}>
                    Lots are created automatically when stock is received.
                  </Typography>
                </Box>
              ) : (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      {['Lot Number', 'Received', 'Expiry', 'Unit Cost', 'Status', 'Stock by Location'].map(h => (
                        <TableCell
                          key={h}
                          sx={{ fontWeight: 600, color: theme.palette.text.secondary, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}
                        >
                          {h}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {lotsResponse.lots.map(lot => (
                      <TableRow key={lot._id} hover>
                        <TableCell>
                          <Typography variant="body2" fontWeight={600}>{lot.lotNumber}</Typography>
                          {lot.supplierBatchRef && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                              Batch: {lot.supplierBatchRef}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {new Date(lot.receivedDate).toLocaleDateString()}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color={lot.expiryDate ? 'text.primary' : 'text.disabled'}>
                            {lot.expiryDate ? new Date(lot.expiryDate).toLocaleDateString() : '—'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{fmt(fromMinorUnitsOrNull(lot.unitCostAtReceipt))}</Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={lot.active ? 'Active' : 'Inactive'}
                            size="small"
                            color={lot.active ? 'success' : 'default'}
                            variant={lot.active ? 'filled' : 'outlined'}
                            sx={{ fontSize: '0.68rem', height: 20 }}
                          />
                        </TableCell>
                        <TableCell>
                          {lot.locations.length === 0 ? (
                            <Typography variant="caption" color="text.disabled">No stock</Typography>
                          ) : (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                              {lot.locations.map(loc => (
                                <Box key={loc.locationId} sx={{ display: 'flex', gap: 0.75, alignItems: 'baseline' }}>
                                  <Typography variant="caption" color="text.secondary">
                                    {loc.locationName ?? resolveLocationName(loc.locationId)}
                                  </Typography>
                                  <Typography variant="caption" fontWeight={700} sx={{ color: qtyColor(loc.quantity) }}>
                                    {formatQty(loc.quantity)}
                                  </Typography>
                                </Box>
                              ))}
                            </Box>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </Collapse>
            <LotsHelpModal open={lotsHelpOpen} onClose={() => setLotsHelpOpen(false)} />
          </Paper>
        );
      })()}

      {/* SIMPLE-WINE.4 §7 — the link flow, opened from the status line. */}
      {item.type === 'WINE'
        && item.executionBehavior !== 'RECIPE_CONSUMPTION'
        && !item.wineId && venueId && itemId && (
        <WineCatalogLinkDialog
          open={wineLinkOpen}
          venueId={venueId}
          itemId={itemId}
          sizeValue={item.sizeValue}
          sizeUnit={item.sizeUnit}
          onLinked={(fresh) => setItem(fresh as typeof item)}
          onClose={() => setWineLinkOpen(false)}
        />
      )}

      {/* Appears on Menus — new Menu system (INVENTORY-MENU-PLACEMENT.2 adds
          an "Add to menu" action for wine-linked items) */}
      <Paper sx={{ borderRadius: 2, overflow: 'hidden', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
        <Box
          sx={{
            px: 3, py: 2,
            borderBottom: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2,
          }}
        >
          <Box>
            <Typography variant="subtitle1" fontWeight={600}>Appears on Menus</Typography>
            <Typography variant="caption" color="text.secondary">
              Menus and wine-list menus this item appears on
            </Typography>
          </Box>
          {item.wineId && (
            <Button
              size="small"
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={openAddToMenu}
              sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2, flexShrink: 0 }}
            >
              Add to Menu
            </Button>
          )}
        </Box>
        {menuLinksLoading ? (
          <Box sx={{ py: 3, display: 'flex', justifyContent: 'center' }}>
            <CircularProgress size={24} />
          </Box>
        ) : menuLinks.length === 0 ? (
          <Box sx={{ py: 3, textAlign: 'center', color: theme.palette.text.secondary }}>
            <Typography variant="body2">This item is not linked to any menu.</Typography>
          </Box>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                {['Menu', 'Type', 'Status', 'Linked via'].map(h => (
                  <TableCell
                    key={h}
                    sx={{ fontWeight: 600, color: theme.palette.text.secondary, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}
                  >
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {menuLinks.map(link => (
                <TableRow key={`${link.menuId}-${link.via}`} hover>
                  <TableCell><Typography variant="body2" fontWeight={500}>{link.menuName}</Typography></TableCell>
                  <TableCell>
                    <Typography variant="caption" color="text.secondary">{link.menuType}</Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={link.menuStatus}
                      size="small"
                      color={link.menuStatus === 'ACTIVE' ? 'success' : 'default'}
                      variant={link.menuStatus === 'ACTIVE' ? 'filled' : 'outlined'}
                      sx={{ fontSize: '0.68rem', height: 20, textTransform: 'capitalize' }}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" color="text.secondary">
                      {link.via === 'item' ? 'Menu item' : 'Serving size'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Paper>

      {/* Suppliers */}
      {!isRecipe && (
      <Paper sx={{ borderRadius: 2, overflow: 'hidden', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
        <Box
          sx={{
            px: 3,
            py: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
          }}
        >
          <Box>
            <Typography variant="subtitle1" fontWeight={600}>Suppliers</Typography>
            <Typography variant="caption" color="text.secondary">Supplier links and receiving memory</Typography>
          </Box>
          <Button
            variant="outlined"
            size="small"
            startIcon={<AddIcon />}
            onClick={openAddSupplierDialog}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            Add Supplier
          </Button>
        </Box>

        {supplierLinksError && (
          <Box sx={{ px: 3, pt: 2 }}>
            <Alert severity="warning" onClose={() => setSupplierLinksError(null)}>{supplierLinksError}</Alert>
          </Box>
        )}

        {supplierLinksLoading ? (
          <Box sx={{ py: 3, display: 'flex', justifyContent: 'center' }}>
            <CircularProgress size={24} />
          </Box>
        ) : supplierLinks.length === 0 ? (
          <Box sx={{ py: 3, textAlign: 'center', color: theme.palette.text.secondary }}>
            <Typography variant="body2">No supplier links yet. Links are created automatically on receiving.</Typography>
          </Box>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                {['Supplier', 'Reference', 'Supplier Code', 'Last Cost', 'Last Received', 'Preferred', 'Active', 'Actions'].map(h => (
                  <TableCell
                    key={h}
                    sx={{
                      fontWeight: 600,
                      color: theme.palette.text.secondary,
                      fontSize: '0.7rem',
                      textTransform: 'uppercase',
                      letterSpacing: '0.04em',
                    }}
                  >
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {supplierLinks.map(link => (
                <TableRow key={link._id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight={500}>{link.supplierName ?? '—'}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color={link.supplierReference ? 'text.primary' : 'text.disabled'}>
                      {link.supplierReference ?? '—'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color={link.supplierItemCode ? 'text.primary' : 'text.disabled'}>
                      {link.supplierItemCode ?? '—'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {/* MONEY-DISPLAY-HOTFIX.1 — lastReceivedCost is minor units */}
                      {link.lastReceivedCost != null ? fmt(fromMinorUnitsOrNull(link.lastReceivedCost)) : '—'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {link.lastReceivedDate
                        ? new Date(link.lastReceivedDate).toLocaleDateString()
                        : '—'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Tooltip title={link.isPreferred ? 'Clear preferred' : 'Set as preferred'}>
                      <IconButton
                        size="small"
                        onClick={() => handleTogglePreferred(link)}
                        sx={{ color: link.isPreferred ? '#f59e0b' : theme.palette.action.disabled }}
                      >
                        {link.isPreferred ? <StarIcon fontSize="small" /> : <StarBorderIcon fontSize="small" />}
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <Switch
                      size="small"
                      checked={link.active}
                      onChange={(_, checked) => handleToggleActive(link, checked)}
                    />
                  </TableCell>
                  <TableCell>
                    <Tooltip title="Edit reference">
                      <IconButton size="small" onClick={() => openEditRef(link)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Paper>
      )}

      {/* Cost (E2) — between stock/balances and Movement History. COSTING.1d:
          gated on stock-cost visibility (admins + entitled VENUE_USERs); the api
          403s the unentitled regardless. Recipe items carry no cost layers. */}
      {!isRecipe && canViewStockCosts() && venueId && itemId && (
        <ItemCostSection venueId={venueId} itemId={itemId} defaultCurrency={defaultCurrency} />
      )}

      {/* Movement History */}
      {!isRecipe && (
      <Paper sx={{ borderRadius: 2, overflow: 'hidden', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
        <Box
          sx={{
            px: 3,
            py: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
          }}
        >
          <Box>
            <Typography variant="subtitle1" fontWeight={600}>Movement History</Typography>
            <Typography variant="caption" color="text.secondary">Append-only — corrections require a reverse adjustment · Transfers appear here as stock-out / stock-in entries</Typography>
          </Box>
          {movTotal > 0 && (
            <Typography variant="caption" color="text.secondary">{movTotal} movements</Typography>
          )}
        </Box>

        {movError && (
          <Box sx={{ px: 3, pt: 2 }}>
            <Alert severity="warning">{movError}</Alert>
          </Box>
        )}

        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                {['Date', 'Type', 'Qty', 'Unit Cost', 'Total Cost', 'Reference', 'Posted By', 'Notes'].map(h => (
                  <TableCell
                    key={h}
                    sx={{
                      fontWeight: 600,
                      color: theme.palette.text.secondary,
                      fontSize: '0.7rem',
                      textTransform: 'uppercase',
                      letterSpacing: '0.04em',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {movLoading ? (
                <TableRow>
                  <TableCell colSpan={8}>
                    <Box sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
                      <CircularProgress size={24} />
                    </Box>
                  </TableCell>
                </TableRow>
              ) : movements.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8}>
                    <Box sx={{ py: 4, textAlign: 'center', color: theme.palette.text.secondary }}>
                      <Typography variant="body2">
                        No movements yet. Post a manual adjustment to set opening stock.
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                movements.map(mov => (
                  <TableRow key={mov._id} hover>
                    <TableCell>
                      <Typography variant="caption" whiteSpace="nowrap">{fmtDate(mov.postedAt)}</Typography>
                    </TableCell>
                    <TableCell>
                      <MovementTypeBadge type={mov.type} />
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        fontWeight={700}
                        sx={{ color: qtyColor(mov.quantity) }}
                      >
                        {mov.quantity > 0 ? `+${formatQty(mov.quantity)}` : formatQty(mov.quantity)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {/* MONEY-DISPLAY-HOTFIX.1 — movement unitCost is minor units */}
                      <Typography variant="body2">{fmt(fromMinorUnitsOrNull(mov.unitCost))}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {/* total derived in minor via costTimesQty, converted at display */}
                        {mov.unitCost != null ? fmt(fromMinorUnitsOrNull(costTimesQty(mov.unitCost, mov.quantity))) : '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {/* POS-ORDER-NUMBER.1 — the order NUMBER when the movement
                          carries one, the id when it does not. This showed the
                          last 6 of an ObjectId, which matches nothing anyone
                          reconciling against the POS can see. Identity stays in
                          the tooltip. */}
                      {(() => {
                        const ref = mov.sourceDocument
                          ? formatSourceRef(mov.sourceDocument.id, mov.sourceDocument.number)
                          : null;
                        return (
                          <Tooltip title={ref?.full ?? ''} placement="top">
                            <Typography variant="caption" color="text.secondary">
                              {mov.sourceDocument
                                ? `${mov.sourceDocument.type.replace(/_/g, ' ')}: ${ref?.text ?? '—'}`
                                : '—'}
                            </Typography>
                          </Tooltip>
                        );
                      })()}
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption">{mov.postedBy}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">{mov.notes ?? '—'}</Typography>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {movTotal > movPageSize && (
          <TablePagination
            component="div"
            count={movTotal}
            page={movPage}
            rowsPerPage={movPageSize}
            rowsPerPageOptions={[25, 50]}
            onPageChange={(_, p) => setMovPage(p)}
            onRowsPerPageChange={e => { setMovPageSize(Number(e.target.value)); setMovPage(0); }}
          />
        )}
      </Paper>
      )}

      {deleteError && <Alert severity="error" onClose={() => setDeleteError(null)}>{deleteError}</Alert>}

      {/* Add Supplier dialog */}
      <Dialog open={addSupplierOpen} onClose={() => setAddSupplierOpen(false)} maxWidth="xs" fullWidth fullScreen={fullScreen}>
        <DialogTitle>Add Supplier</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: '16px !important' }}>
          {addSupplierError && <Alert severity="error">{addSupplierError}</Alert>}
          {suppliersLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress size={24} />
            </Box>
          ) : availableSuppliers.length === 0 ? (
            <Alert severity="info">
              No suppliers are connected to this venue yet. Ask an admin to create or connect a supplier first.
            </Alert>
          ) : (
            <>
              <FormControl fullWidth size="small" required>
                <InputLabel>Supplier</InputLabel>
                <Select
                  value={newSupplierId}
                  label="Supplier"
                  onChange={e => setNewSupplierId(e.target.value)}
                >
                  {availableSuppliers.map(s => (
                    <MenuItem key={s._id} value={s._id}>{s.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                label="Supplier Reference"
                size="small"
                fullWidth
                value={newSupplierRef}
                onChange={e => setNewSupplierRef(e.target.value)}
                placeholder="e.g. SKU or account ref (optional)"
              />
              <TextField
                label="Supplier Item Code"
                size="small"
                fullWidth
                value={newSupplierCode}
                onChange={e => setNewSupplierCode(e.target.value)}
                placeholder="e.g. supplier's product code (optional)"
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddSupplierOpen(false)} sx={{ textTransform: 'none' }} disabled={addSupplierSaving}>
            Cancel
          </Button>
          <Button
            onClick={handleAddSupplier}
            variant="contained"
            disabled={addSupplierSaving || !newSupplierId}
            sx={{ textTransform: 'none', fontWeight: 600 }}
            startIcon={addSupplierSaving ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {addSupplierSaving ? 'Adding…' : 'Add Supplier'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Supplier Details dialog */}
      <Dialog open={editRefOpen} onClose={() => setEditRefOpen(false)} maxWidth="xs" fullWidth fullScreen={fullScreen}>
        <DialogTitle>Edit Supplier Details</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: '16px !important' }}>
          {editRefError && <Alert severity="error">{editRefError}</Alert>}
          <TextField
            label="Supplier Reference"
            size="small"
            fullWidth
            value={editRefValue}
            onChange={e => setEditRefValue(e.target.value)}
            placeholder="e.g. SKU or account ref"
            autoFocus
          />
          <TextField
            label="Supplier Item Code"
            size="small"
            fullWidth
            value={editCodeValue}
            onChange={e => setEditCodeValue(e.target.value)}
            placeholder="e.g. supplier's product code"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditRefOpen(false)} sx={{ textTransform: 'none' }} disabled={editRefSaving}>
            Cancel
          </Button>
          <Button
            onClick={handleEditRef}
            variant="contained"
            disabled={editRefSaving}
            sx={{ textTransform: 'none', fontWeight: 600 }}
            startIcon={editRefSaving ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {editRefSaving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Recompute balance dialog */}
      <Dialog open={recomputeOpen} onClose={() => setRecomputeOpen(false)} maxWidth="xs" fullWidth fullScreen={fullScreen}>
        <DialogTitle>Recompute Stock Balance</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '16px !important' }}>
          {!recomputeResult ? (
            <>
              <FormControl fullWidth size="small" required>
                <InputLabel>Location</InputLabel>
                <Select
                  value={recomputeLocationId}
                  label="Location"
                  onChange={e => setRecomputeLocationId(e.target.value)}
                >
                  {locations.map(l => (
                    <MenuItem key={l._id} value={l._id}>{l.displayName}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <DialogContentText>
                This will recalculate the cached stock quantity for{' '}
                <strong>{item?.displayName ?? 'this item'}</strong> at the selected
                location by replaying its movement history. The cached quantity will be overwritten.
                Movement history is not changed.
              </DialogContentText>
            </>
          ) : (
            <Box sx={{ pt: 1 }}>
              <Alert severity={recomputeResult.difference === 0 ? 'success' : 'warning'} sx={{ mb: 2 }}>
                {recomputeResult.difference === 0
                  ? 'Balance was already correct — no change needed.'
                  : `Balance updated (difference: ${recomputeResult.difference > 0 ? '+' : ''}${recomputeResult.difference}).`}
              </Alert>
              {[
                { label: 'Before', value: recomputeResult.before },
                { label: 'After',  value: recomputeResult.after  },
              ].map(({ label, value }) => (
                <Box key={label} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                  <Typography variant="body2" color="text.secondary">{label}</Typography>
                  <Typography variant="body2" fontWeight={600}>{value}</Typography>
                </Box>
              ))}
            </Box>
          )}
          {recomputeError && <Alert severity="error" sx={{ mt: 2 }}>{recomputeError}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRecomputeOpen(false)} sx={{ textTransform: 'none' }} disabled={recomputing}>
            {recomputeResult ? 'Close' : 'Cancel'}
          </Button>
          {!recomputeResult && (
            <Button
              onClick={handleRecompute}
              variant="contained"
              disabled={recomputing || !recomputeLocationId}
              sx={{ textTransform: 'none', fontWeight: 600 }}
              startIcon={recomputing ? <CircularProgress size={16} color="inherit" /> : <RecalcIcon />}
            >
              {recomputing ? 'Recomputing…' : 'Recompute Balance'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {venueId && itemId && item && (
        <ItemDeactivationBlockedDialog
          open={blockedOpen}
          onClose={() => setBlockedOpen(false)}
          venueId={venueId}
          itemId={itemId}
          displayName={item.displayName}
          quantity={blockedQty}
        />
      )}

      {/* ITEM-ACTIVE.4 — the confirm dialog that stood here is gone. The menu
          names the act in words before anything happens, which is the same
          deliberateness a confirm provided, minus a press; and the flip is
          undoable for six seconds afterwards. The doors dialog still stands in
          the one case that is NOT reversible by a toast — stock in the way. */}

      {/* Add to menu dialog (INVENTORY-MENU-PLACEMENT.2) — writes a kind=WINE
          MenuItem, never a legacy WineListItem. */}
      <Dialog
        open={addToMenuOpen}
        onClose={() => { if (!addingToMenu) setAddToMenuOpen(false); }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 600 }}>Add to Menu</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Place this wine on one of the venue's menus.
          </Typography>

          {addToMenuError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setAddToMenuError('')}>
              {addToMenuError}
            </Alert>
          )}

          {menusLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress size={24} />
            </Box>
          ) : menus.length === 0 ? (
            <Alert severity="info" icon={false}>
              No active menus found for this venue. Create a menu first under the Menus tab.
            </Alert>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Menu</InputLabel>
                <Select
                  value={selectedMenuId}
                  label="Menu"
                  onChange={e => { setSelectedMenuId(e.target.value); setAddToMenuError(''); }}
                >
                  <MenuItem value="">— Select a menu —</MenuItem>
                  {menus.map(m => (
                    <MenuItem key={m._id} value={m._id}>{m.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth size="small">
                <InputLabel>Visibility</InputLabel>
                <Select
                  value={menuItemVisibility}
                  label="Visibility"
                  onChange={e => setMenuItemVisibility(e.target.value as MenuVisibility)}
                >
                  <MenuItem value="HIDDEN">Hidden</MenuItem>
                  <MenuItem value="LIVE">Live</MenuItem>
                </Select>
              </FormControl>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button onClick={() => setAddToMenuOpen(false)} sx={{ textTransform: 'none' }} disabled={addingToMenu}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleAddToMenu}
            disabled={addingToMenu || !selectedMenuId || menusLoading || !menuWine}
            startIcon={addingToMenu ? <CircularProgress size={16} color="inherit" /> : null}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            {addingToMenu ? 'Adding…' : 'Add to Menu'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Public QR page dialog (INVENTORY-SHORTID-VISIBILITY.1) */}
      <Dialog open={qrOpen} onClose={() => setQrOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <QrIcon fontSize="small" sx={{ color: '#0e7490' }} />
          Public QR Page
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '8px !important' }}>
          {/* QR preview (INVENTORY-QR-DOWNLOAD.1) — encodes the public /i/ URL. */}
          {qrDataUrl && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
              <Box
                component="img"
                src={qrDataUrl}
                alt={`QR code for ${qrUrl}`}
                sx={{
                  width: 168, height: 168,
                  p: 1, backgroundColor: '#fff',
                  border: `1px solid ${alpha(theme.palette.divider, 0.4)}`,
                  borderRadius: 1.5,
                }}
              />
            </Box>
          )}
          <Box>
            <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: '0.04em' }}>
              Short ID
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
              <Box component="span" sx={{ fontFamily: 'monospace', fontWeight: 600, letterSpacing: '0.06em' }}>
                {item.shortId}
              </Box>
              <Tooltip title="Copy short ID">
                <IconButton size="small" onClick={() => copyText(item.shortId as string, 'Short ID')}>
                  <CopyIcon sx={{ fontSize: 15 }} />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: '0.04em' }}>
              Public link
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
              <Link href={qrUrl} target="_blank" rel="noopener" variant="body2" sx={{ wordBreak: 'break-all' }}>
                {qrUrl}
              </Link>
              <Tooltip title="Copy link">
                <IconButton size="small" onClick={() => copyText(qrUrl, 'QR link')}>
                  <CopyIcon sx={{ fontSize: 15 }} />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={downloadQr}
            disabled={!qrDataUrl}
            startIcon={<DownloadIcon />}
            sx={{ textTransform: 'none' }}
          >
            Download QR
          </Button>
          <Box sx={{ flex: 1 }} />
          <Button onClick={() => copyText(qrUrl, 'QR link')} sx={{ textTransform: 'none' }}>
            Copy link
          </Button>
          <Button
            variant="contained"
            component={Link}
            href={qrUrl}
            target="_blank"
            rel="noopener"
            startIcon={<OpenIcon />}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            Open page
          </Button>
        </DialogActions>
      </Dialog>

      {venueId && itemId && item && (
        <OpeningStockDialog
          open={openingStockOpen}
          onClose={() => setOpeningStockOpen(false)}
          venueId={venueId}
          itemId={itemId}
          itemName={item.displayName}
          defaultCurrency={defaultCurrency}
          onSuccess={handleOpeningStockSuccess}
        />
      )}

      <Snackbar
        open={!!copiedMsg}
        autoHideDuration={2000}
        onClose={() => setCopiedMsg(null)}
        message={copiedMsg ?? ''}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />

      {undoToast}
    </Box>
  );
};

export default InventoryItemDetail;
